import React, { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Plus, 
  User, 
  Building, 
  Phone, 
  Mail, 
  Wallet, 
  Globe, 
  Link, 
  Trash2, 
  Save, 
  Download,
  MapPin,
  Maximize2,
  Minimize2,
  Upload,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Search,
  Filter
} from 'lucide-react';

interface Entity {
  id: string;
  type: 'person' | 'company' | 'phone' | 'email' | 'wallet' | 'website' | 'location';
  label: string;
  value: string;
  x: number;
  y: number;
  color: string;
  metadata?: Record<string, any>;
}

interface Connection {
  id: string;
  sourceId: string;
  targetId: string;
  type: 'works_for' | 'owns' | 'contact' | 'located_at' | 'related_to';
  label: string;
  color: string;
  weight?: number;
}

interface MapState {
  entities: Entity[];
  connections: Connection[];
  zoom: number;
  pan: { x: number; y: number };
  name: string;
  createdAt: string;
  updatedAt: string;
}

const OSINTMapping: React.FC = () => {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [isAddingEntity, setIsAddingEntity] = useState(false);
  const [newEntityType, setNewEntityType] = useState<Entity['type']>('person');
  const [newEntityValue, setNewEntityValue] = useState('');
  const [newConnectionType, setNewConnectionType] = useState<Connection['type']>('related_to');
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [connectionStart, setConnectionStart] = useState<Entity | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{ x: number; y: number } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<Entity['type'] | 'all'>('all');
  const [mapName, setMapName] = useState('Nouvelle carte');
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const entityTypes = [
    { type: 'person', label: 'Personne', icon: User, color: 'bg-blue-500' },
    { type: 'company', label: 'Société', icon: Building, color: 'bg-green-500' },
    { type: 'phone', label: 'Téléphone', icon: Phone, color: 'bg-purple-500' },
    { type: 'email', label: 'Email', icon: Mail, color: 'bg-red-500' },
    { type: 'wallet', label: 'Wallet', icon: Wallet, color: 'bg-orange-500' },
    { type: 'website', label: 'Site Web', icon: Globe, color: 'bg-indigo-500' },
    { type: 'location', label: 'Localisation', icon: MapPin, color: 'bg-yellow-500' }
  ];

  const connectionTypes = [
    { type: 'works_for', label: 'Travaille pour', color: '#60A5FA' },
    { type: 'owns', label: 'Possède', color: '#34D399' },
    { type: 'contact', label: 'Contact', color: '#A78BFA' },
    { type: 'located_at', label: 'Situé à', color: '#FBBF24' },
    { type: 'related_to', label: 'Lié à', color: '#9CA3AF' }
  ];

  // Sauvegarde automatique
  useEffect(() => {
    const saveToLocalStorage = () => {
      const mapState: MapState = {
        entities,
        connections,
        zoom,
        pan,
        name: mapName,
        createdAt: localStorage.getItem('osint-map-created') || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('osint-map-current', JSON.stringify(mapState));
    };

    const timer = setTimeout(saveToLocalStorage, 1000);
    return () => clearTimeout(timer);
  }, [entities, connections, zoom, pan, mapName]);

  // Charger depuis localStorage au montage
  useEffect(() => {
    const savedMap = localStorage.getItem('osint-map-current');
    if (savedMap) {
      try {
        const mapState: MapState = JSON.parse(savedMap);
        setEntities(mapState.entities || []);
        setConnections(mapState.connections || []);
        setZoom(mapState.zoom || 1);
        setPan(mapState.pan || { x: 0, y: 0 });
        setMapName(mapState.name || 'Nouvelle carte');
      } catch (error) {
        console.error('Erreur lors du chargement de la carte:', error);
      }
    }
  }, []);

  const handleAddEntity = () => {
    if (!newEntityValue.trim()) return;

    const entity: Entity = {
      id: `entity-${Date.now()}`,
      type: newEntityType,
      label: newEntityValue,
      value: newEntityValue,
      x: Math.random() * 400 + 100,
      y: Math.random() * 300 + 100,
      color: entityTypes.find(t => t.type === newEntityType)?.color || 'bg-gray-500'
    };

    setEntities(prev => [...prev, entity]);
    setNewEntityValue('');
    setIsAddingEntity(false);
  };

  const handleSaveMap = () => {
    const mapState: MapState = {
      entities,
      connections,
      zoom,
      pan,
      name: mapName,
      createdAt: localStorage.getItem('osint-map-created') || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(mapState, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mapName.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadMap = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const mapState: MapState = JSON.parse(e.target?.result as string);
        setEntities(mapState.entities || []);
        setConnections(mapState.connections || []);
        setZoom(mapState.zoom || 1);
        setPan(mapState.pan || { x: 0, y: 0 });
        setMapName(mapState.name || 'Carte importée');
      } catch (error) {
        console.error('Erreur lors du chargement du fichier:', error);
        alert('Erreur lors du chargement du fichier');
      }
    };
    reader.readAsText(file);
  };

  const handleExportPNG = useCallback(() => {
    if (!canvasRef.current) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 1920;
    canvas.height = 1080;
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Dessiner les connexions
    connections.forEach(connection => {
      const source = entities.find(e => e.id === connection.sourceId);
      const target = entities.find(e => e.id === connection.targetId);
      if (!source || !target) return;

      ctx.beginPath();
      ctx.moveTo(source.x, source.y);
      ctx.lineTo(target.x, target.y);
      ctx.strokeStyle = connection.color;
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    // Dessiner les entités
    entities.forEach(entity => {
      ctx.beginPath();
      ctx.arc(entity.x, entity.y, 20, 0, 2 * Math.PI);
      ctx.fillStyle = entity.color.replace('bg-', '#');
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Ajouter le texte
      ctx.fillStyle = '#ffffff';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(entity.label, entity.x, entity.y + 35);
    });

    // Télécharger
    const link = document.createElement('a');
    link.download = `${mapName.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
    link.href = canvas.toDataURL();
    link.click();
  }, [entities, connections, mapName]);

  const handleZoom = (delta: number) => {
    setZoom(prev => Math.max(0.25, Math.min(3, prev + delta)));
  };

  const handleResetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const filteredEntities = entities.filter(entity => {
    const matchesSearch = entity.label.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || entity.type === filterType;
    return matchesSearch && matchesFilter;
  });



  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.ctrlKey)) { // Middle click ou Ctrl+click pour pan
      e.preventDefault();
      setIsPanning(true);
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        setPanStart({ 
          x: e.clientX - rect.left - pan.x, 
          y: e.clientY - rect.top - pan.y 
        });
      }
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (isPanning && panStart && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setPan({
        x: e.clientX - rect.left - panStart.x,
        y: e.clientY - rect.top - panStart.y
      });
    }
  };

  const handleCanvasMouseUp = () => {
    setIsPanning(false);
    setPanStart(null);
  };

  const handleEntityDragStart = (e: React.MouseEvent, entity: Entity) => {
    e.preventDefault();
    e.stopPropagation();
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setDragStart({ 
        x: (e.clientX - rect.left - pan.x) / zoom - entity.x, 
        y: (e.clientY - rect.top - pan.y) / zoom - entity.y
      });
      setIsDragging(true);
    }
  };

  const handleEntityDrag = (e: React.MouseEvent, entity: Entity) => {
    if (!dragStart || !isDragging || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const newX = Math.max(30, (e.clientX - rect.left - pan.x) / zoom - dragStart.x);
    const newY = Math.max(30, (e.clientY - rect.top - pan.y) / zoom - dragStart.y);

    setEntities(prev => 
      prev.map(e => 
        e.id === entity.id ? { ...e, x: newX, y: newY } : e
      )
    );
  };

  const handleEntityDragEnd = () => {
    setDragStart(null);
    setIsDragging(false);
  };

  const handleEntityClick = (e: React.MouseEvent, entity: Entity) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (connectionStart && connectionStart.id !== entity.id) {
      // Créer la connexion
              const connection: Connection = {
          id: `connection-${Date.now()}`,
          sourceId: connectionStart.id,
          targetId: entity.id,
          type: newConnectionType,
          label: connectionTypes.find(t => t.type === newConnectionType)?.label || 'Lié à',
          color: connectionTypes.find(t => t.type === newConnectionType)?.color || '#9CA3AF'
        };
      setConnections(prev => [...prev, connection]);
      setConnectionStart(null);
    }
  };

  const handleEntityRightClick = (e: React.MouseEvent, entity: Entity) => {
    e.preventDefault();
    e.stopPropagation();
    setConnectionStart(entity);
  };

  const deleteEntity = (entityId: string) => {
    setEntities(prev => prev.filter(e => e.id !== entityId));
    setConnections(prev => prev.filter(c => c.sourceId !== entityId && c.targetId !== entityId));
  };



  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getEntityIcon = (type: Entity['type']) => {
    const entityType = entityTypes.find(t => t.type === type);
    const Icon = entityType?.icon || User;
    return <Icon className="w-4 h-4" />;
  };

  const getConnectionPath = (source: Entity, target: Entity) => {
    const dx = target.x - source.x;
    const dy = target.y - source.y;
    const angle = Math.atan2(dy, dx);
    
    // Calculer les points de départ et d'arrivée
    const startX = source.x + 30 * Math.cos(angle);
    const startY = source.y + 30 * Math.sin(angle);
    const endX = target.x - 30 * Math.cos(angle);
    const endY = target.y - 30 * Math.sin(angle);
    
    // Créer une ligne droite complète
    return `M ${startX} ${startY} L ${endX} ${endY}`;
  };

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'h-full'} flex flex-col bg-osint-dark`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-osint-light-gray bg-osint-gray">
        <div className="flex items-center space-x-4">
          <input
            type="text"
            value={mapName}
            onChange={(e) => setMapName(e.target.value)}
            className="text-xl font-bold text-white bg-transparent border-none outline-none focus:bg-osint-light-gray px-2 py-1 rounded"
          />
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Globe className="w-4 h-4" />
            <span>{entities.length} entités</span>
            <span>•</span>
            <Link className="w-4 h-4" />
            <span>{connections.length} connexions</span>
            <span>•</span>
            <span>Zoom: {(zoom * 100).toFixed(0)}%</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1 bg-osint-light-gray rounded-lg p-1">
            <button 
              onClick={() => handleZoom(-0.25)}
              className="p-1 hover:bg-gray-600 rounded transition-colors"
              title="Zoom arrière"
            >
              <ZoomOut className="w-4 h-4 text-white" />
            </button>
            <button 
              onClick={() => handleZoom(0.25)}
              className="p-1 hover:bg-gray-600 rounded transition-colors"
              title="Zoom avant"
            >
              <ZoomIn className="w-4 h-4 text-white" />
            </button>
            <button 
              onClick={handleResetView}
              className="p-1 hover:bg-gray-600 rounded transition-colors"
              title="Réinitialiser vue"
            >
              <RotateCcw className="w-4 h-4 text-white" />
            </button>
          </div>
          
          {/* Boutons compacts en mode fenêtré */}
          {!isFullscreen ? (
            <div className="flex items-center space-x-1">
              <button 
                onClick={toggleFullscreen}
                className="p-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white transition-colors"
                title="Plein écran"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="p-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white transition-colors"
                title="Importer"
              >
                <Upload className="w-4 h-4" />
              </button>
              <button 
                onClick={handleSaveMap}
                className="p-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white transition-colors"
                title="Sauvegarder"
              >
                <Save className="w-4 h-4" />
              </button>
              <button 
                onClick={handleExportPNG}
                className="p-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white transition-colors"
                title="Export PNG"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          ) : (
            /* Boutons avec texte en plein écran */
            <>
              <button 
                onClick={toggleFullscreen}
                className="flex items-center space-x-2 px-3 py-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
              >
                <Minimize2 className="w-4 h-4" />
                <span>Réduire</span>
              </button>
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center space-x-2 px-3 py-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
              >
                <Upload className="w-4 h-4" />
                <span>Importer</span>
              </button>
              <button 
                onClick={handleSaveMap}
                className="flex items-center space-x-2 px-3 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>Sauvegarder</span>
              </button>
              <button 
                onClick={handleExportPNG}
                className="flex items-center space-x-2 px-3 py-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export PNG</span>
              </button>
            </>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleLoadMap}
            className="hidden"
          />
        </div>
      </div>

      <div className="flex-1 flex">
                {/* Sidebar */}
        <div className={`${isFullscreen ? 'w-96' : 'w-72'} bg-osint-gray border-r border-osint-light-gray p-3 space-y-4 overflow-y-auto`}>
          {/* Recherche et filtres */}
          <div className="osint-card p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">Recherche & Filtres</h3>
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher une entité..."
                  className="osint-input w-full pl-10 pr-4 py-2 text-sm"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as Entity['type'] | 'all')}
                  className="osint-input flex-1 text-sm"
                >
                  <option value="all">Tous les types</option>
                  {entityTypes.map(type => (
                    <option key={type.type} value={type.type}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="osint-card p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">Instructions</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <p>• <strong>Clic gauche</strong> : Déplacer l'entité</p>
              <p>• <strong>Clic droit</strong> : Commencer une connexion</p>
              <p>• <strong>Molette</strong> : Zoom</p>
              <p>• <strong>Ctrl+Clic</strong> : Déplacer la vue</p>
            </div>
            {connectionStart && (
              <div className="mt-3 p-2 bg-purple-900/20 border border-purple-500/50 rounded-lg">
                <p className="text-xs text-purple-300">
                  Connexion en cours : {connectionStart.label} → Cliquez sur une autre entité
                </p>
              </div>
            )}
          </div>

          {/* Ajouter une entité */}
          <div className="osint-card p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">Ajouter une entité</h3>
            
            {!isAddingEntity ? (
              <button
                onClick={() => setIsAddingEntity(true)}
                className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-osint-orange hover:bg-orange-600 rounded-lg text-white font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Nouvelle entité</span>
              </button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
                  <select
                    value={newEntityType}
                    onChange={(e) => setNewEntityType(e.target.value as Entity['type'])}
                    className="osint-input w-full px-3 py-2 rounded-lg text-white"
                  >
                    {entityTypes.map(type => (
                      <option key={type.type} value={type.type}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Valeur</label>
                  <input
                    type="text"
                    value={newEntityValue}
                    onChange={(e) => setNewEntityValue(e.target.value)}
                    placeholder="Entrez la valeur..."
                    className="osint-input w-full px-3 py-2 rounded-lg text-white placeholder-gray-400"
                  />
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={handleAddEntity}
                    className="flex-1 py-2 px-3 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                  >
                    Ajouter
                  </button>
                  <button
                    onClick={() => setIsAddingEntity(false)}
                    className="py-2 px-3 bg-gray-600 hover:bg-gray-700 rounded-lg text-white text-sm transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Type de connexion */}
          <div className="osint-card p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">Type de connexion</h3>
            <select
              value={newConnectionType}
              onChange={(e) => setNewConnectionType(e.target.value as Connection['type'])}
              className="osint-input w-full px-3 py-2 rounded-lg text-white"
            >
              {connectionTypes.map(type => (
                <option key={type.type} value={type.type}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Liste des entités */}
          <div className="osint-card p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">
              Entités ({filteredEntities.length}/{entities.length})
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {filteredEntities.map(entity => (
                <div key={entity.id} className="flex items-center justify-between p-2 bg-osint-light-gray rounded-lg hover:bg-gray-600 transition-colors">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${entity.color}`}></div>
                    <div className="flex flex-col">
                      <span className="text-sm text-white">{entity.label}</span>
                      <span className="text-xs text-gray-400">{entityTypes.find(t => t.type === entity.type)?.label}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteEntity(entity.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {filteredEntities.length === 0 && (
                <div className="text-center text-gray-400 py-4">
                  Aucune entité trouvée
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative bg-osint-dark overflow-hidden">
          <div
            ref={canvasRef}
            className="w-full h-full relative cursor-grab active:cursor-grabbing"
            style={{ 
              backgroundImage: 'radial-gradient(circle, #2D2D2D 1px, transparent 1px)', 
              backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
              backgroundPosition: `${pan.x}px ${pan.y}px`
            }}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={(e) => {
              handleCanvasMouseMove(e);
              if (isDragging && dragStart && canvasRef.current) {
                const rect = canvasRef.current.getBoundingClientRect();
                const mouseX = (e.clientX - rect.left - pan.x) / zoom;
                const mouseY = (e.clientY - rect.top - pan.y) / zoom;
                
                const activeEntity = entities.find(entity => 
                  Math.abs(entity.x - mouseX + dragStart.x) < 50 && 
                  Math.abs(entity.y - mouseY + dragStart.y) < 50
                );
                if (activeEntity) {
                  handleEntityDrag(e, activeEntity);
                }
              }
            }}
            onMouseUp={() => {
              handleEntityDragEnd();
              handleCanvasMouseUp();
            }}
            onWheel={(e) => {
              e.preventDefault();
              const delta = e.deltaY > 0 ? -0.1 : 0.1;
              handleZoom(delta);
            }}
          >
            {/* Connexions */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              {connections.map(connection => {
                const source = entities.find(e => e.id === connection.sourceId);
                const target = entities.find(e => e.id === connection.targetId);
                if (!source || !target) return null;

                return (
                  <g key={connection.id}>
                    <path
                      d={getConnectionPath(source, target)}
                      stroke={connection.color}
                      strokeWidth="3"
                      strokeDasharray="10,5"
                      fill="none"
                      markerEnd="url(#arrowhead)"
                    />
                  </g>
                );
              })}
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="#666" />
                </marker>
              </defs>
            </svg>

            {/* Entités */}
            {filteredEntities.map(entity => {
              const isHighlighted = searchTerm && entity.label.toLowerCase().includes(searchTerm.toLowerCase());
              return (
                <div
                  key={entity.id}
                  className={`absolute ${entity.color} flex flex-col items-center cursor-move shadow-lg border-2 transition-all ${
                    connectionStart?.id === entity.id ? 'ring-2 ring-purple-400 border-purple-400' : 
                    isHighlighted ? 'ring-2 ring-yellow-400 border-yellow-400' :
                    'border-white/20 hover:border-white/40'
                  } ${isDragging ? 'z-50' : ''}`}
                  style={{ 
                    left: entity.x * zoom + pan.x - 30, 
                    top: entity.y * zoom + pan.y - 30,
                    opacity: searchTerm && !isHighlighted ? 0.3 : 1,
                    transform: `scale(${zoom})`
                  }}
                  onMouseDown={(e) => handleEntityDragStart(e, entity)}
                  onMouseMove={(e) => handleEntityDrag(e, entity)}
                  onMouseUp={handleEntityDragEnd}
                  onClick={(e) => handleEntityClick(e, entity)}
                  onContextMenu={(e) => handleEntityRightClick(e, entity)}
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white">
                    {getEntityIcon(entity.type)}
                  </div>
                  <div className="mt-1 px-2 py-1 bg-black/80 rounded text-xs text-white text-center max-w-32 truncate">
                    {entity.label}
                  </div>
                </div>
              );
            })}
            
            {/* Affichage des entités filtrées cachées */}
            {searchTerm && (
              <div className="absolute top-4 left-4 bg-black/80 text-white px-3 py-2 rounded-lg text-sm">
                {filteredEntities.length} / {entities.length} entités affichées
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OSINTMapping; 